import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Tooltip, Typography } from '@mui/material'
import { DropListButton, Icon, ListItemText, ActionSheetItem, TextInput, theme, Button } from '@ketch-com/deck'
import { useIdentitySpaces } from 'api/identitySpaces/queries/useIdentitySpaces'
import { RoutesManager } from 'utils/routing/routesManager'
import { useNavigate } from 'react-router-dom'
import { useAuditLogContext } from 'pages/insights/auditLog/context/AuditLogContext'
import { throttle } from 'lodash'

/**
 * Identifier dropdown and input for querying the audit log.
 *
 * Uses query params to persist state choices and allowing sharing.
 */
export const AuditLogFilters: React.FC<{}> = () => {
  const { refetch, queries, setMultipleQueryParams, isLoading, isRefetching } = useAuditLogContext()
  const navigate = useNavigate()
  const [searchString, setSearchString] = useState(queries.identifierSearch || '')
  const [selectedIdentitySpaceCode, setSelectedIdentitySpaceCode] = useState(queries.identitySpaceCode || '')
  const { data: identitySpaces } = useIdentitySpaces()

  // Identifier click handler
  const handleDropdownClick = useCallback(
    (code: string) => {
      setSelectedIdentitySpaceCode(code)
    },
    [setSelectedIdentitySpaceCode],
  )

  // Identifier dropdown items
  const identitySpaceDropdownItems = useMemo(
    () =>
      (identitySpaces || []).map(({ code, name }) => ({
        content: name,
        code,
        onClick: () => handleDropdownClick(code),
      })),
    [identitySpaces, handleDropdownClick],
  )

  // Current identifier value
  const dropListButtonCurrentValue = useMemo(
    () => identitySpaceDropdownItems.find(item => item.code === selectedIdentitySpaceCode),
    [identitySpaceDropdownItems, selectedIdentitySpaceCode],
  )

  // Throttled refetch to prevent spam requests but allow refetching for the same ID every 5 seconds
  const throttledRefetch = useMemo(
    () =>
      throttle(refetch, 5000, {
        leading: true, // Run immediately
        trailing: false, // Do not run again after delay
      }),
    [refetch],
  )

  // Search button click handler
  const handleSubmitSearch = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault()
      setMultipleQueryParams([
        { key: 'identifierSearch', value: searchString },
        { key: 'identitySpaceCode', value: selectedIdentitySpaceCode },
        { key: 'permitDateTime', value: 'latest' },
      ])
      throttledRefetch()
    },
    [searchString, selectedIdentitySpaceCode, setMultipleQueryParams, throttledRefetch],
  )

  // Enter key handler
  const handleSearchInputOnKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLImageElement>) => {
      if (e.key === 'Enter' && searchString && selectedIdentitySpaceCode) {
        setMultipleQueryParams([
          { key: 'identifierSearch', value: searchString },
          { key: 'identitySpaceCode', value: selectedIdentitySpaceCode },
          { key: 'permitDateTime', value: 'latest' },
        ])
        throttledRefetch()
      }
    },
    [searchString, selectedIdentitySpaceCode, setMultipleQueryParams, throttledRefetch],
  )

  useEffect(() => {
    setSearchString(queries.identifierSearch || '')
  }, [queries])

  return (
    <Box display="flex" alignItems="flex-end" gap={1}>
      {/* Select */}
      <DropListButton
        label={
          <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
            <Typography variant="label">Identifier</Typography>
            <Tooltip
              title={
                <>
                  The{' '}
                  <Button variant="link" onClick={() => navigate(RoutesManager.settings.identitySpaces.root.getURL())}>
                    <Typography
                      color={theme.palette.White.o100}
                      variant="smallBody"
                      sx={{ textDecoration: 'underline' }}
                    >
                      Identifier
                    </Typography>
                  </Button>{' '}
                  to search for in the audit log.
                </>
              }
            >
              <Box height={16}>
                <Icon name="FUnknown" iconColor={theme.palette.Black.o48} width={16} height={16} />
              </Box>
            </Tooltip>
          </Box>
        }
        width="300px"
        options={identitySpaceDropdownItems}
        placeholder="Select Identifier"
        getOptionLabel={option => option.content}
        isOptionEqualToValue={(option, value) => option.code === value.code}
        disableClearable
        onChange={(_, newValue) => {
          if (newValue) {
            const currentItem = identitySpaceDropdownItems.find(item => item.code === newValue.code)
            if (currentItem) {
              currentItem.onClick && currentItem.onClick()
              throttledRefetch.cancel() // Allow immediate refetching when the dropdown is changed
            }
          }
        }}
        value={{
          code: dropListButtonCurrentValue?.code || '',
          content: dropListButtonCurrentValue?.content || '',
          onClick: dropListButtonCurrentValue?.onClick,
        }}
        renderOption={(props, option) => (
          <ActionSheetItem selected={option.code === selectedIdentitySpaceCode} {...props}>
            <ListItemText selected={option.code === selectedIdentitySpaceCode}>{option.content}</ListItemText>
          </ActionSheetItem>
        )}
        sx={{
          mr: 2,
          '& .MuiFormControl-root .MuiInputBase-root': {
            height: '44px',
          },
          '& .MuiInputBase-root': {
            border: `1px solid ${theme.palette.Common.Divider}`,
            borderRadius: '5px',
          },
        }}
      />

      {/* Search Input */}
      <TextInput
        label={
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="label">Identifier Value</Typography>
            <Tooltip title="The value of the identifier to search for in the audit log.">
              <Box height={16}>
                <Icon name="FUnknown" iconColor={theme.palette.Black.o48} width={16} height={16} />
              </Box>
            </Tooltip>
          </Box>
        }
        value={searchString}
        disabled={!selectedIdentitySpaceCode}
        startIcon={<Icon name="OMag" />}
        endIcon={searchString && <Icon sx={{ cursor: 'pointer' }} name="OCross" onClick={() => setSearchString('')} />}
        placeholder="Enter Identifier Value"
        onChange={event => {
          setSearchString(event.target.value)
          throttledRefetch.cancel() // Allow immediate refetching when the input is changed
        }}
        onKeyDown={handleSearchInputOnKeyPress}
        sx={{
          width: '394px',
        }}
      />

      {/* Submit Button */}
      <Button
        onClick={handleSubmitSearch}
        disabled={!searchString}
        pending={isLoading || isRefetching}
        size="large"
        sx={{ height: '44px' }}
      >
        Search
      </Button>
    </Box>
  )
}
